import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import GreetingHeader from "../../shared/GreetingHeader";
import {
  fetchDashboardMetrics,
  selectDashboardMetrics,
  selectDashboardLoading,
  selectDashboardError,
} from "../../../ReduxSlices/Admin/dashboardSlice";
import LoadingDots from "../../shared/LoadingDots";

const AdminDashboard = ({ user, navigateTo }) => {
  const dispatch = useDispatch();
  const metrics = useSelector(selectDashboardMetrics);
  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);

  // ✅ Fetch initial metrics on mount
  useEffect(() => {
    if (!metrics) {
      dispatch(fetchDashboardMetrics());
    }
  }, [dispatch, metrics]);

  return (
    <div className="p-6">
      <GreetingHeader name={user?.name} />

      <div className="labdashboard grid grid-cols-4 sm:grid-cols-2 gap-6">
        {/* ✅ Quick Actions */}
        <div className="quick-actions pending p-6 mx-auto col-span-full sm:col-span-2 col-start-1">
          <div className="mx-auto">
            <h3 className="text-lg font-semibold">Quick Actions</h3>
          </div>

          <div className="flex flex-wrap mt-[10px] gap-[10px]">
            <button
              type="button"
              onClick={() => navigateTo("reports")}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-3 rounded-lg shadow transition-all duration-200"
            >
               View Reports
            </button>

            <button
              type="button"
              onClick={() => navigateTo("users")}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-3 rounded-lg shadow transition-all duration-200"
            >
              User Administration
            </button>

            <button
              type="button"
              onClick={() => navigateTo("pricing")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-5 py-3 rounded-lg shadow transition-all duration-200"
            >
             Services Offered
            </button>
          </div>
        </div>

        {/* ✅ Patients Served */}
        <div className="pending rounded-xl shadow-md p-6 col-span-1 sm:col-span-2 col-start-1">
          <h3 className="text-lg font-semibold mb-2">Patients Served</h3>

          {loading ? (
            <div className="flex justify-center py-2">
              <LoadingDots height={10} width={30} color="#38AD3E" />
            </div>
          ) : error ? (
            <p className="text-red-600">Error: {error}</p>
          ) : metrics?.total_patients_served ? (
            <ul className="report-list">
              <li className="med-list-content">
                Today: <strong>{metrics.total_patients_served.today || 0}</strong>
              </li>
              <li className="med-list-content">
                This Week: <strong>{metrics.total_patients_served.week || 0}</strong>
              </li>
              <li className="med-list-content">
                This Month: <strong>{metrics.total_patients_served.month || 0}</strong>
              </li>
            </ul>
          ) : (
            <p className="text-gray-600 italic">No data available.</p>
          )}
        </div>

        {/* ✅ Medications Dispensed */}
        <div className="pending rounded-xl shadow-md p-6 col-span-1 sm:col-span-2">
          <h3 className="text-lg font-semibold mb-2">Medications Dispensed</h3>

          {loading ? (
            <div className="flex justify-center py-2">
              <LoadingDots height={10} width={30} color="#38AD3E" />
            </div>
          ) : error ? (
            <p className="text-red-600">Error: {error}</p>
          ) : metrics?.total_drugs_dispensed ? (
            <ul className="report-list">
              <li className="med-list-content">
                Today: <strong>{metrics.total_drugs_dispensed.today || 0}</strong>
              </li>
              <li className="med-list-content">
                This Week: <strong>{metrics.total_drugs_dispensed.week || 0}</strong>
              </li>
              <li className="med-list-content">
                This Month: <strong>{metrics.total_drugs_dispensed.month || 0}</strong>
              </li>
            </ul>
          ) : (
            <p className="text-gray-600 italic">No data available.</p>
          )}
        </div>

        {/* ✅ Low Stock Medications */}
        <div className="pending rounded-xl shadow-md p-6 col-span-1 sm:col-span-2">
          <h3 className="text-lg font-semibold mb-2">Low Stock Medications</h3>

          {loading ? (
            <div className="flex justify-center py-2">
              <LoadingDots />
            </div>
          ) : error ? (
            <p className="text-red-600">Error: {error}</p>
          ) : metrics?.low_stock_medications?.length ? (
            <ul className="report-list">
              {metrics.low_stock_medications.map((med, idx) => (
                <li key={idx} className="med-list-content flex flex-col">
                  <span><strong>{med.name}</strong></span>
                  <span className="text-sm text-gray-600">Batch: {med.batch_number}</span>
                  <span className="text-sm text-gray-600">Code: {med.medication_code}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 italic">All medications are sufficiently stocked.</p>
          )}
        </div>

        {/* ✅ Expired Medications */}
        <div className="pending rounded-xl shadow-md p-6 col-span-1 sm:col-span-2">
          <h3 className="text-lg font-semibold mb-2">Expired Medications</h3>

          {loading ? (
            <div className="flex justify-center py-2">
              <LoadingDots />
            </div>
          ) : error ? (
            <p className="text-red-600">Error: {error}</p>
          ) : metrics?.expired_medications?.length ? (
            <ul className="report-list">
              {metrics.expired_medications.map((med, idx) => (
                <li key={idx} className="med-list-content flex flex-col">
                  <span><strong>{med.name}</strong></span>
                  <span className="text-sm text-gray-600">Batch: {med.batch_number}</span>
                  <span className="text-sm text-gray-600">Code: {med.medication_code}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 italic">No expired medications.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
